import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash, CaretDown, CaretRight, MagnifyingGlass, DotsThree, ListChecks, FolderSimplePlus, X, Table, Kanban, CalendarBlank, Funnel, SlidersHorizontal, ChatCircleText } from "@phosphor-icons/react";
import { StatusEditor, PriorityEditor, AssigneeEditor, DateEditor, ProgressBar, STATUS, STATUS_ORDER, DEFAULT_STATUSES, PRIORITY, PRIORITY_ORDER, StatusChip, PriorityFlag } from "../components/pm/pm";
import { TaskDrawer } from "../components/pm/TaskDrawer";
import { KanbanView, CalendarView, TimelineView } from "../components/pm/views";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const FONT = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const ROW = "flex items-center gap-2 px-3";
const W = { status: "w-[132px]", priority: "w-[118px]", assignee: "w-[92px]", due: "w-[108px]", progress: "w-[150px]" };

function Skeleton() {
  return (
    <div className="space-y-2 p-4" data-testid="pm-skeleton">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 animate-pulse">
          <div className="w-4 h-4 rounded bg-neutral-200" />
          <div className="h-4 bg-neutral-200 rounded flex-1" />
          <div className="h-5 w-24 bg-neutral-200 rounded-md" />
          <div className="h-5 w-16 bg-neutral-200 rounded" />
          <div className="w-6 h-6 rounded-full bg-neutral-200" />
        </div>
      ))}
    </div>
  );
}

export default function Tareas() {
  const { user } = useAuth();
  const role = user?.role;
  const canManage = role !== "contratista";
  const canDelete = ["admin", "gerente_general"].includes(role) || !!user?.can_delete;

  const [meta, setMeta] = useState({ users: [] });
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [groups, setGroups] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [collapsed, setCollapsed] = useState(new Set());
  const [drawerId, setDrawerId] = useState(null);
  const [search, setSearch] = useState("");
  const [dq, setDq] = useState("");
  const [view, setView] = useState("tabla");
  const [filters, setFilters] = useState({ mine: false, overdue: false, noAssignee: false, status: [], priority: [], assignee: [], tags: [] });
  const [cols, setCols] = useState({ status: true, priority: true, assignee: true, due: true, progress: true });
  const [calMonth, setCalMonth] = useState(new Date());
  const searchRef = useRef(null);

  useEffect(() => { const t = setTimeout(() => setDq(search.trim().toLowerCase()), 250); return () => clearTimeout(t); }, [search]);

  const users = meta.users || [];
  const statuses = active?.statuses?.length ? active.statuses : DEFAULT_STATUSES;
  const isDone = (k) => !!(statuses.find((s) => s.key === k) || {}).done;
  const [mgrOpen, setMgrOpen] = useState(false);

  const refreshTasks = useCallback(async (pid) => {
    const id = pid || active?.id; if (!id) return;
    const { data } = await api.get(`/pm/tasks?project_id=${id}`);
    setTasks(data);
  }, [active?.id]);

  const loadProject = useCallback(async (pid) => {
    setLoading(true);
    try {
      const [g, t] = await Promise.all([api.get(`/pm/projects/${pid}/groups`), api.get(`/pm/tasks?project_id=${pid}`)]);
      setGroups(g.data); setTasks(t.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      if (role === "contratista") { setLoading(false); return; }
      try {
        const [m, p] = await Promise.all([api.get("/pm/meta"), api.get("/pm/projects")]);
        setMeta(m.data); setProjects(p.data);
        if (p.data.length) { setActive(p.data[0]); await loadProject(p.data[0].id); }
        else setLoading(false);
      } catch (e) { setLoading(false); }
    })();
  }, [role, loadProject]);

  const selectProject = async (p) => { setActive(p); setSelected(new Set()); setDrawerId(null); await loadProject(p.id); };

  const addProject = async () => {
    const name = window.prompt("Nombre del nuevo proyecto:"); if (!name || !name.trim()) return;
    const { data } = await api.post("/pm/projects", { name: name.trim() });
    setProjects((ps) => [...ps, data]); selectProject(data);
  };
  const addGroup = async () => {
    if (!active) return;
    const name = window.prompt("Nombre del grupo:"); if (!name || !name.trim()) return;
    const { data } = await api.post("/pm/groups", { project_id: active.id, name: name.trim() });
    setGroups((gs) => [...gs, data]);
  };

  const patchTask = async (id, fields) => {
    setTasks((ts) => ts.map((t) => {
      if (t.id === id) return { ...t, ...fields };
      if ((t.subtasks || []).some((s) => s.id === id)) return { ...t, subtasks: t.subtasks.map((s) => (s.id === id ? { ...s, ...fields } : s)) };
      return t;
    }));
    try { await api.patch(`/pm/tasks/${id}`, fields); await refreshTasks(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); refreshTasks(); }
  };

  const quickAdd = async (groupId, title) => {
    if (!title.trim() || !active) return;
    try { await api.post("/pm/tasks", { project_id: active.id, group_id: groupId, title: title.trim() }); await refreshTasks(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeTask = async (id) => {
    if (!window.confirm("¿Eliminar esta tarea y sus subtareas?")) return;
    setTasks((ts) => ts.filter((t) => t.id !== id));
    if (drawerId === id) setDrawerId(null);
    try { await api.delete(`/pm/tasks/${id}`); await refreshTasks(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); refreshTasks(); }
  };

  const bulk = async (action, value) => {
    const ids = [...selected]; if (!ids.length) return;
    if (action === "delete" && !window.confirm(`¿Eliminar ${ids.length} tarea(s)?`)) return;
    try { await api.post("/pm/tasks/bulk", { ids, action, value }); setSelected(new Set()); await refreshTasks(); toast.success("Acción aplicada"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addSubtask = async (parentId, groupId, title) => {
    try { await api.post("/pm/tasks", { project_id: active.id, group_id: groupId, parent_id: parentId, title }); await refreshTasks(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addComment = async (id, text, mentions) => { try { await api.post(`/pm/tasks/${id}/comments`, { text, mentions: mentions || [] }); await refreshTasks(); toast.success("Comentario agregado"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delComment = async (id, cid) => { try { await api.delete(`/pm/tasks/${id}/comments/${cid}`); await refreshTasks(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const saveStatuses = async (list) => {
    try { const { data } = await api.put(`/pm/projects/${active.id}/statuses`, { statuses: list }); setActive(data); setProjects((ps) => ps.map((p) => (p.id === data.id ? data : p))); setMgrOpen(false); await refreshTasks(); toast.success("Estados actualizados"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleGroup = (gid) => setCollapsed((s) => { const n = new Set(s); n.has(gid) ? n.delete(gid) : n.add(gid); return n; });

  const toggleArr = (key, val) => setFilters((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));
  const today = new Date().toISOString().slice(0, 10);
  const passFilters = (t) => {
    if (dq && !((t.title || "").toLowerCase().includes(dq) || (t.description || "").toLowerCase().includes(dq) || (t.tags || []).join(" ").toLowerCase().includes(dq) || (t.assignee_names || []).join(" ").toLowerCase().includes(dq))) return false;
    if (filters.mine && !(t.assignee_ids || []).includes(user?.id)) return false;
    if (filters.overdue && !(t.due_date && t.due_date < today && t.status !== "completada")) return false;
    if (filters.noAssignee && (t.assignee_ids || []).length) return false;
    if (filters.status.length && !filters.status.includes(t.status)) return false;
    if (filters.priority.length && !filters.priority.includes(t.priority)) return false;
    if (filters.assignee.length && !(t.assignee_ids || []).some((a) => filters.assignee.includes(a))) return false;
    if (filters.tags.length && !(t.tags || []).some((x) => filters.tags.includes(x))) return false;
    return true;
  };
  const allTags = useMemo(() => [...new Set(tasks.flatMap((t) => t.tags || []))], [tasks]);
  const filtered = useMemo(() => tasks.filter(passFilters), [tasks, dq, filters, user?.id]);
  const activeFilterCount = filters.status.length + filters.priority.length + filters.assignee.length + filters.tags.length + (filters.mine ? 1 : 0) + (filters.overdue ? 1 : 0) + (filters.noAssignee ? 1 : 0);
  const tasksByGroup = useMemo(() => {
    const map = {};
    groups.forEach((g) => (map[g.id] = []));
    filtered.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).forEach((t) => { (map[t.group_id] || (map[t.group_id] = [])).push(t); });
    return map;
  }, [filtered, groups]);

  const drawerTask = useMemo(() => tasks.find((t) => t.id === drawerId) || null, [tasks, drawerId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") { e.preventDefault(); searchRef.current?.focus(); } if (e.key === "Escape") { setDrawerId(null); setSelected(new Set()); } };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (role === "contratista") return <div className="p-8 text-neutral-500" style={FONT}>Este módulo no está disponible para tu rol.</div>;

  return (
    <div style={FONT} data-testid="pm-module">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-neutral-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Pendientes</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Gestiona proyectos, grupos y tareas. Edita todo en línea.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-white focus-within:ring-1 focus-within:ring-primary/40">
            <MagnifyingGlass size={15} className="text-neutral-400" />
            <input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar tareas… ( / )" data-testid="pm-search" className="text-sm outline-none bg-transparent w-40 sm:w-56" />
            {search && <button onClick={() => setSearch("")}><X size={13} className="text-neutral-400" /></button>}
          </div>
        </div>
      </div>

      {/* Project pills */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1" data-testid="pm-projects">
        {projects.map((p) => (
          <button key={p.id} onClick={() => selectProject(p)} data-testid={`project-${p.id}`}
            className={`inline-flex items-center gap-2 h-8 px-3 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-150 border ${active?.id === p.id ? "bg-primary text-white border-primary" : "bg-white text-neutral-600 border-input hover:bg-neutral-50"}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: active?.id === p.id ? "#fff" : p.color }} /> {p.name}
          </button>
        ))}
        {canManage && <button onClick={addProject} data-testid="add-project-btn" className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-sm font-medium text-primary border border-dashed border-primary/40 hover:bg-primary/5 transition-colors whitespace-nowrap"><Plus size={14} weight="bold" /> Proyecto</button>}
      </div>

      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="inline-flex items-center rounded-md border border-input bg-white p-0.5" data-testid="view-switcher">
          {[["tabla", Table, "Tabla"], ["kanban", Kanban, "Kanban"], ["calendario", CalendarBlank, "Calendario"], ["timeline", ListChecks, "Timeline"]].map(([v, Ic, lb]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`} className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-[5px] text-sm font-medium transition-colors duration-150 ${view === v ? "bg-primary text-white" : "text-neutral-600 hover:bg-neutral-100"}`}><Ic size={15} weight={view === v ? "fill" : "regular"} />{lb}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="filters-btn" className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-sm font-medium transition-colors duration-150 ${activeFilterCount ? "border-primary text-primary bg-primary/5" : "border-input text-neutral-600 hover:bg-neutral-50"}`}><Funnel size={15} weight={activeFilterCount ? "fill" : "regular"} /> Filtros{activeFilterCount > 0 && <span className="ml-0.5 text-[10px] bg-primary text-white rounded-full px-1.5">{activeFilterCount}</span>}</button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-3 space-y-3 max-h-[70vh] overflow-y-auto">
              {[["mine", "Mis tareas"], ["overdue", "Vencidas"], ["noAssignee", "Sin responsable"]].map(([k, lb]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={filters[k]} onChange={() => setFilters((f) => ({ ...f, [k]: !f[k] }))} className="accent-primary" data-testid={`filter-${k}`} />{lb}</label>
              ))}
              <div><p className="text-xs font-semibold uppercase tracking-[0.05em] text-neutral-400 mb-1">Estado</p>{statuses.map((s) => (<label key={s.key} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer"><input type="checkbox" checked={filters.status.includes(s.key)} onChange={() => toggleArr("status", s.key)} className="accent-primary" />{<StatusChip value={s.key} statuses={statuses} />}</label>))}</div>
              <div><p className="text-xs font-semibold uppercase tracking-[0.05em] text-neutral-400 mb-1">Prioridad</p>{PRIORITY_ORDER.map((p) => (<label key={p} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer"><input type="checkbox" checked={filters.priority.includes(p)} onChange={() => toggleArr("priority", p)} className="accent-primary" /><PriorityFlag value={p} showLabel /></label>))}</div>
              <div><p className="text-xs font-semibold uppercase tracking-[0.05em] text-neutral-400 mb-1">Responsable</p>{users.map((u) => (<label key={u.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer"><input type="checkbox" checked={filters.assignee.includes(u.id)} onChange={() => toggleArr("assignee", u.id)} className="accent-primary" /><span className="truncate">{u.name}</span></label>))}</div>
              {allTags.length > 0 && <div><p className="text-xs font-semibold uppercase tracking-[0.05em] text-neutral-400 mb-1">Etiquetas</p>{allTags.map((tg) => (<label key={tg} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer"><input type="checkbox" checked={filters.tags.includes(tg)} onChange={() => toggleArr("tags", tg)} className="accent-primary" />{tg}</label>))}</div>}
              {activeFilterCount > 0 && <button onClick={() => setFilters({ mine: false, overdue: false, noAssignee: false, status: [], priority: [], assignee: [], tags: [] })} className="text-xs text-red-600 font-medium hover:underline">Limpiar filtros</button>}
            </PopoverContent>
          </Popover>
          {canManage && <button onClick={() => setMgrOpen(true)} data-testid="manage-statuses-btn" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input text-sm text-neutral-600 hover:bg-neutral-50 transition-colors"><DotsThree size={16} weight="bold" /> Estados</button>}
          {view === "tabla" && (
            <Popover>
              <PopoverTrigger asChild><button data-testid="cols-btn" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input text-sm text-neutral-600 hover:bg-neutral-50 transition-colors"><SlidersHorizontal size={15} /> Columnas</button></PopoverTrigger>
              <PopoverContent align="end" className="w-44 p-2">{Object.entries({ status: "Estado", priority: "Prioridad", assignee: "Responsable", due: "Límite", progress: "Progreso" }).map(([k, lb]) => (<label key={k} className="flex items-center gap-2 px-1 py-1.5 text-sm cursor-pointer"><input type="checkbox" checked={cols[k]} onChange={() => setCols((c) => ({ ...c, [k]: !c[k] }))} className="accent-primary" data-testid={`col-${k}`} />{lb}</label>))}</PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {view === "tabla" && (
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {/* Column headers */}
        <div className={`${ROW} h-9 border-b border-border bg-neutral-50/60 text-xs font-semibold uppercase tracking-[0.05em] text-neutral-500`}>
          <span className="w-[26px]" />
          <span className="flex-1">Tarea</span>
          {cols.status && <span className={W.status}>Estado</span>}
          {cols.priority && <span className={W.priority}>Prioridad</span>}
          {cols.assignee && <span className={W.assignee}>Resp.</span>}
          {cols.due && <span className={`${W.due} hidden md:block`}>Límite</span>}
          {cols.progress && <span className={`${W.progress} hidden md:block`}>Progreso</span>}
          <span className="w-[32px] hidden md:block" />
        </div>

        {loading ? <Skeleton /> : (
          groups.length === 0 ? (
            <div className="p-10 text-center text-neutral-500 text-sm">No hay grupos. Crea uno para empezar.</div>
          ) : groups.map((g) => {
            const list = tasksByGroup[g.id] || [];
            const done = list.filter((t) => isDone(t.status)).length;
            const isCol = collapsed.has(g.id);
            return (
              <div key={g.id} data-testid={`group-${g.id}`}>
                <div className="flex items-center gap-2 px-3 h-10 bg-neutral-50/40 border-b border-border sticky top-0 z-10">
                  <button onClick={() => toggleGroup(g.id)} data-testid={`group-collapse-${g.id}`} className="text-neutral-400 hover:text-neutral-700 transition-colors">{isCol ? <CaretRight size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}</button>
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: g.color }} />
                  <span className="font-semibold text-sm text-neutral-800">{g.name}</span>
                  <span className="text-xs text-neutral-400">{done}/{list.length}</span>
                </div>
                {!isCol && (
                  <div>
                    {list.map((t) => (
                      <div key={t.id} className={`${ROW} min-h-[42px] border-b border-neutral-100 group transition-colors duration-150 ${selected.has(t.id) ? "bg-orange-50/50" : "hover:bg-neutral-50/80"}`} data-testid={`task-row-${t.id}`}>
                        <span className="w-[26px] flex justify-center"><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSel(t.id)} data-testid={`task-check-${t.id}`} className="w-3.5 h-3.5 accent-primary cursor-pointer" /></span>
                        <button onClick={() => setDrawerId(t.id)} data-testid={`task-open-${t.id}`} className="flex-1 text-left min-w-0 flex items-center gap-2 py-1">
                  <span className={`text-sm truncate ${isDone(t.status) ? "line-through text-neutral-400" : "text-neutral-800"}`}>{t.title}</span>
                          {t.subtask_total > 0 && <span className="text-[10px] text-neutral-400 shrink-0 border border-neutral-200 rounded px-1">{t.subtask_done}/{t.subtask_total}</span>}
                          {(t.comments || []).length > 0 && <span className="text-[10px] text-neutral-400 shrink-0 inline-flex items-center gap-0.5"><ChatCircleText size={11} />{(t.comments || []).length}</span>}
                        </button>
                        {cols.status && <div className={W.status}><StatusEditor value={t.status} statuses={statuses} onChange={(v) => patchTask(t.id, { status: v })} testid={`cell-status-${t.id}`} /></div>}
                        {cols.priority && <div className={W.priority}><PriorityEditor value={t.priority} onChange={(v) => patchTask(t.id, { priority: v })} testid={`cell-priority-${t.id}`} /></div>}
                        {cols.assignee && <div className={W.assignee}><AssigneeEditor value={t.assignee_ids || []} names={t.assignee_names || []} users={users} onChange={(ids) => patchTask(t.id, { assignee_ids: ids })} testid={`cell-assignee-${t.id}`} /></div>}
                        {cols.due && <div className={`${W.due} hidden md:block`}><DateEditor value={t.due_date} onChange={(v) => patchTask(t.id, { due_date: v })} testid={`cell-due-${t.id}`} /></div>}
                        {cols.progress && <div className={`${W.progress} hidden md:block`}><ProgressBar value={t.subtask_total ? t.subtask_pct : (isDone(t.status) ? 100 : (t.progress || 0))} /></div>}
                        <div className="w-[32px] hidden md:flex justify-end">
                          {canDelete && <button onClick={() => removeTask(t.id)} data-testid={`task-del-${t.id}`} className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600 transition-all"><Trash size={15} /></button>}
                        </div>
                      </div>
                    ))}
                    {canManage && <QuickAdd onAdd={(title) => quickAdd(g.id, title)} testid={`quick-add-${g.id}`} />}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      )}

      {view === "kanban" && (loading ? <Skeleton /> : <KanbanView tasks={filtered} statuses={statuses} onStatusChange={(id, s) => patchTask(id, { status: s })} onOpen={setDrawerId} />)}
      {view === "calendario" && (loading ? <Skeleton /> : <CalendarView tasks={filtered} statuses={statuses} onOpen={setDrawerId} month={calMonth} setMonth={setCalMonth} />)}
      {view === "timeline" && (loading ? <Skeleton /> : <TimelineView tasks={filtered} statuses={statuses} onOpen={setDrawerId} />)}

      {view === "tabla" && canManage && !loading && groups.length > 0 && (
        <button onClick={addGroup} data-testid="add-group-btn" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 hover:text-primary transition-colors"><FolderSimplePlus size={16} /> Agregar grupo</button>
      )}

      {/* Floating mass action bar */}
      <div className={`fixed left-1/2 -translate-x-1/2 bottom-6 z-40 transition-all duration-300 ${selected.size ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0 pointer-events-none"}`}>
        <div className="flex items-center gap-1 rounded-xl bg-neutral-900/95 backdrop-blur-xl border border-white/10 shadow-2xl text-white px-2 py-1.5" data-testid="bulk-bar">
          <span className="text-xs font-semibold px-2">{selected.size} sel.</span>
          <BulkMenu label="Estado" testid="bulk-status" options={statuses.map((s) => ({ k: s.key, node: <StatusChip value={s.key} statuses={statuses} /> }))} onPick={(v) => bulk("status", v)} />
          <BulkMenu label="Prioridad" testid="bulk-priority" options={Object.keys(PRIORITY).map((k) => ({ k, node: <span className="text-sm" style={{ color: PRIORITY[k].color }}>{PRIORITY[k].label}</span> }))} onPick={(v) => bulk("priority", v)} />
          <BulkMenu label="Grupo" testid="bulk-group" options={groups.map((g) => ({ k: g.id, node: <span className="text-sm">{g.name}</span> }))} onPick={(v) => bulk("group", v)} />
          {canDelete && <button onClick={() => bulk("delete")} data-testid="bulk-delete" className="flex items-center gap-1 text-sm px-2.5 py-1 rounded-md hover:bg-red-500/80 transition-colors"><Trash size={14} /> Eliminar</button>}
          <button onClick={() => setSelected(new Set())} className="p-1 rounded-md hover:bg-white/10"><X size={15} /></button>
        </div>
      </div>

      {mgrOpen && <StatusManager statuses={statuses} onSave={saveStatuses} onClose={() => setMgrOpen(false)} />}

      <TaskDrawer open={!!drawerTask} task={drawerTask} users={users} allTasks={tasks} statuses={statuses} canManage={canManage} currentUser={user}
        onClose={() => setDrawerId(null)}
        onPatch={(f) => patchTask(drawerId, f)}
        addSubtask={(title) => addSubtask(drawerId, drawerTask?.group_id, title)}
        patchSubtask={(sid, f) => patchTask(sid, f)}
        deleteSubtask={(sid) => removeTask(sid)}
        addComment={(text, mentions) => addComment(drawerId, text, mentions)}
        delComment={(cid) => delComment(drawerId, cid)} />
    </div>
  );
}

function StatusManager({ statuses, onSave, onClose }) {
  const [list, setList] = useState(statuses.map((s) => ({ ...s })));
  const set = (i, k, v) => setList((l) => l.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const move = (i, d) => setList((l) => { const j = i + d; if (j < 0 || j >= l.length) return l; const n = [...l]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const add = () => setList((l) => [...l, { key: "", label: "Nuevo estado", color: "#6B7280", done: false }]);
  const del = (i) => setList((l) => l.filter((_, j) => j !== i));
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()} data-testid="status-manager" style={FONT}>
        <div className="flex items-center justify-between mb-3"><h3 className="text-lg font-semibold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Estados del proyecto</h3><button onClick={onClose}><X size={18} /></button></div>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {list.map((s, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`status-row-${i}`}>
              <input type="color" value={s.color} onChange={(e) => set(i, "color", e.target.value)} className="w-8 h-8 rounded cursor-pointer border border-input p-0.5" />
              <input value={s.label} onChange={(e) => set(i, "label", e.target.value)} data-testid={`status-label-${i}`} className="flex-1 text-sm border border-input rounded-sm px-2 py-1.5 outline-none focus:ring-1 focus:ring-primary/40" placeholder="Nombre del estado" />
              <label className="flex items-center gap-1 text-[11px] text-neutral-500 shrink-0"><input type="checkbox" checked={s.done} onChange={(e) => set(i, "done", e.target.checked)} className="accent-primary" /> Completa</label>
              <button onClick={() => move(i, -1)} className="text-neutral-400 hover:text-neutral-700 text-xs px-1">▲</button>
              <button onClick={() => move(i, 1)} className="text-neutral-400 hover:text-neutral-700 text-xs px-1">▼</button>
              <button onClick={() => del(i)} className="text-neutral-400 hover:text-red-600" data-testid={`status-del-${i}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-4">
          <button onClick={add} data-testid="status-add" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"><Plus size={14} weight="bold" /> Agregar estado</button>
          <button onClick={() => onSave(list)} data-testid="status-save" className="h-9 px-4 bg-primary text-white rounded-md text-sm font-semibold hover:bg-orange-600 transition-colors">Guardar estados</button>
        </div>
      </div>
    </div>
  );
}

function QuickAdd({ onAdd, testid }) {
  const [v, setV] = useState("");
  return (
    <div className="flex items-center gap-2 px-3 h-10 hover:bg-neutral-50/60 transition-colors">
      <Plus size={15} className="text-neutral-300 ml-[26px]" />
      <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && v.trim()) { onAdd(v.trim()); setV(""); } }} placeholder="Agregar tarea…" data-testid={testid} className="flex-1 text-sm outline-none bg-transparent placeholder:text-neutral-400" />
    </div>
  );
}

function BulkMenu({ label, options, onPick, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid={testid} className="flex items-center gap-1 text-sm px-2.5 py-1 rounded-md hover:bg-white/10 transition-colors"><DotsThree size={14} /> {label}</button>
      {open && (
        <div className="absolute bottom-full mb-2 left-0 bg-white text-neutral-800 rounded-md shadow-xl border border-border p-1 min-w-[160px] max-h-56 overflow-y-auto" onMouseLeave={() => setOpen(false)}>
          {options.map((o) => <button key={o.k} onClick={() => { onPick(o.k); setOpen(false); }} className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-neutral-100 transition-colors">{o.node}</button>)}
        </div>
      )}
    </div>
  );
}
